/**
 * Browser HMAC provider — synchronous HMAC-SHA256 for the browser.
 *
 * # Why this file exists
 *
 * Phase A's save layer signs and verifies envelopes via the `HmacProvider`
 * interface. Phase A shipped two implementations:
 *   - `nodeHmacProvider` (in `@aevons-wake/core/save/integrity.node`) —
 *     uses `node:crypto`, only loadable in Node. Used by tests.
 *   - This file's `browserHmacProvider` — uses `@noble/hashes`, runs in
 *     any JavaScript runtime (browser, Worker, Edge, Deno, Bun, Node).
 *     Used by the production browser bundle.
 *
 * Both must produce **byte-identical output** for the same key + message
 * inputs. Otherwise saves written in production cannot be verified by
 * server-side replay (Stage 1+ anti-cheat) and saves recovered from
 * tests cannot be loaded in production. The `__tests__/browserHmacProvider.test.ts`
 * file proves this empirically via cross-provider round-trip tests.
 *
 * # Why @noble/hashes and not crypto.subtle
 *
 * Phase A's `HmacProvider` interface is **synchronous** — `sign()` returns
 * a hex string, `verify()` returns a boolean. The browser's `crypto.subtle.sign`
 * is async (returns a Promise<ArrayBuffer>). Using crypto.subtle would force
 * an async refactor of the entire save layer (`attachChecksum`, `verifyChecksum`,
 * everything that calls them). That's a much larger blast radius than pulling
 * in a small (~5 KB) audited library. @noble/hashes is sync, audited,
 * widely deployed (used by ethers.js, viem, web3 ecosystem broadly), and
 * actively maintained.
 *
 * # Why @noble/hashes 2.x specifically
 *
 * Pinned via packages/ui/package.json. The 2.x major reorganized module
 * paths (e.g., `./hmac` became `./hmac.js`, sha256/sha384/sha512 collapsed
 * into `./sha2.js`) but did not change function signatures or output bytes.
 * The cross-provider tests prove output identity with Node's crypto.
 *
 * # Why a hand-rolled timing-safe compare
 *
 * @noble/hashes 2.x does not export a timing-safe equal helper, and
 * `crypto.subtle.timingSafeEqual` doesn't exist (only `node:crypto` has it).
 * The constant-time XOR-OR-accumulator pattern below is the textbook
 * implementation — used by NaCl, libsodium reference implementations,
 * and most HMAC verification code in the wild. It does NOT short-circuit
 * on first mismatch (the bug timing-safe compare exists to prevent).
 *
 * @see {@link "@aevons-wake/core/save/integrity"} — HmacProvider interface
 * @see {@link "@aevons-wake/core/save/integrity.node"} — Node sibling impl
 */

import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import type { HmacProvider } from '@aevons-wake/core';

/**
 * Browser-safe HMAC-SHA256 provider.
 *
 * Singleton — stateless, no instance needed. Same shape as `nodeHmacProvider`.
 *
 * @example
 * import { browserHmacProvider } from '@/persistence/browserHmacProvider';
 * const sig = browserHmacProvider.sign(key, message);
 * const ok = browserHmacProvider.verify(key, message, sig);
 */
export const browserHmacProvider: HmacProvider = {
  /**
   * Compute HMAC-SHA256 over `message` with `key`. Returns 64-character
   * lowercase hex (matches Node provider's output format).
   *
   * Inputs are `Uint8Array` — caller is responsible for any text → bytes
   * encoding (the save layer uses TextEncoder before calling this).
   */
  sign(key: Uint8Array, message: Uint8Array): string {
    const macBytes = hmac(sha256, key, message);
    return bytesToHex(macBytes);
  },

  /**
   * Recompute HMAC-SHA256 and timing-safe compare to `expected`.
   *
   * Defensive: returns false on any malformed `expected` (wrong length,
   * non-hex characters). This prevents an attacker from passing a crafted
   * non-hex string that throws inside hexToBytes and reaches an outer
   * catch block they could observe.
   *
   * Timing-safe: comparison runs in constant time relative to the inputs,
   * so an attacker cannot infer how many leading bytes of their forged
   * checksum match the real one by measuring response time.
   */
  verify(key: Uint8Array, message: Uint8Array, expected: string): boolean {
    // Defensive: HMAC-SHA256 hex output is always exactly 64 chars.
    if (expected.length !== 64) {
      return false;
    }
    // Defensive: hex-only. Prevents hexToBytes from throwing on bad input.
    if (!/^[0-9a-f]{64}$/.test(expected)) {
      return false;
    }

    const expectedBytes = hexToBytes(expected);
    const actualBytes = hmac(sha256, key, message);

    return timingSafeEqual(actualBytes, expectedBytes);
  },
};

/**
 * Constant-time byte-array equality.
 *
 * Returns true iff `a` and `b` have the same length and all bytes match.
 * Runs in O(min(a.length, b.length)) time regardless of where the first
 * mismatch occurs — does NOT short-circuit.
 *
 * Pattern: XOR each byte pair, OR-accumulate the differences. Final
 * result is 0 iff every pair matched. The compiler/runtime cannot
 * legitimately optimize this into a short-circuit because every iteration
 * unconditionally writes to `diff` based on data values.
 *
 * Length-mismatch returns false immediately — this is intentional and
 * not a timing leak: signature length is public information (always 32
 * bytes for HMAC-SHA256), so leaking "wrong length" tells an attacker
 * nothing they didn't already know.
 */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) {
    return false;
  }
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    // Non-null assertion safe: i bounded by a.length, b same length.
    diff |= a[i]! ^ b[i]!;
  }
  return diff === 0;
}